import type { SwitchboardFlowData } from '../converters/shared/reactFlowTypes'
import {
  getPersistedGraphSignature,
  type SignatureRefContext,
} from '../utils/switchboardFlowSignature'

export interface SwitchboardSnapshot {
  updated_at?: string
  json_data?: unknown
}

export interface ResolvePostSaveFlowDataParams {
  localFlowData: SwitchboardFlowData
  preSaveBaseUpdatedAt?: string
  mutationUpdatedAt?: string
  refetchedSwitchboard?: SwitchboardSnapshot | null
  parseTimestamp: (value?: string) => number | null
  deserializeSwitchboardData: (jsonData: unknown) => SwitchboardFlowData
  refContext?: SignatureRefContext
}

type ResolveReason =
  | 'matched_refetch'
  | 'differing_refetch'
  | 'missing_refetch'
  | 'invalid_refetch_payload'
  | 'refetch_parse_failed'
  | 'pending_signature'

export interface ResolvePostSaveFlowDataResult {
  flowData: SwitchboardFlowData
  editorBaseUpdatedAt?: string
  hydratedFromRefetch: boolean
  reason: ResolveReason
}

function pickNewerUpdatedAt(
  first: string | undefined,
  second: string | undefined,
  parseTimestamp: (value?: string) => number | null,
): string | undefined {
  if (!first) return second
  if (!second) return first

  const firstMs = parseTimestamp(first)
  const secondMs = parseTimestamp(second)

  if (firstMs === null && secondMs === null) {
    return second
  }
  if (firstMs === null) {
    return second
  }
  if (secondMs === null) {
    return first
  }

  return secondMs >= firstMs ? second : first
}

export function resolvePostSaveFlowData({
  localFlowData,
  preSaveBaseUpdatedAt,
  mutationUpdatedAt,
  refetchedSwitchboard,
  parseTimestamp,
  deserializeSwitchboardData,
  refContext,
}: ResolvePostSaveFlowDataParams): ResolvePostSaveFlowDataResult {
  const localEditorBaseUpdatedAt = pickNewerUpdatedAt(
    preSaveBaseUpdatedAt,
    mutationUpdatedAt,
    parseTimestamp,
  )
  const localSignature = getPersistedGraphSignature(localFlowData, refContext)
  if (localSignature.status === 'pending') {
    return {
      flowData: localFlowData,
      editorBaseUpdatedAt: localEditorBaseUpdatedAt,
      hydratedFromRefetch: false,
      reason: 'pending_signature',
    }
  }

  if (!refetchedSwitchboard) {
    return {
      flowData: localFlowData,
      editorBaseUpdatedAt: localEditorBaseUpdatedAt,
      hydratedFromRefetch: false,
      reason: 'missing_refetch',
    }
  }

  const refetchedUpdatedAt = refetchedSwitchboard.updated_at
  const refetchedJsonData = refetchedSwitchboard.json_data

  if (
    refetchedJsonData === undefined ||
    refetchedJsonData === null ||
    (typeof refetchedJsonData === 'string' && refetchedJsonData.trim() === '')
  ) {
    return {
      flowData: localFlowData,
      editorBaseUpdatedAt: localEditorBaseUpdatedAt,
      hydratedFromRefetch: false,
      reason: 'invalid_refetch_payload',
    }
  }

  try {
    const refetchedFlowData = deserializeSwitchboardData(refetchedJsonData)
    const refetchedSignature = getPersistedGraphSignature(
      refetchedFlowData,
      refContext,
    )
    if (refetchedSignature.status === 'pending') {
      return {
        flowData: localFlowData,
        editorBaseUpdatedAt: localEditorBaseUpdatedAt,
        hydratedFromRefetch: false,
        reason: 'pending_signature',
      }
    }

    if (refetchedSignature.value !== localSignature.value) {
      return {
        flowData: localFlowData,
        editorBaseUpdatedAt: localEditorBaseUpdatedAt,
        hydratedFromRefetch: false,
        reason: 'differing_refetch',
      }
    }

    return {
      flowData: localFlowData,
      editorBaseUpdatedAt: pickNewerUpdatedAt(
        localEditorBaseUpdatedAt,
        refetchedUpdatedAt,
        parseTimestamp,
      ),
      hydratedFromRefetch: false,
      reason: 'matched_refetch',
    }
  } catch {
    return {
      flowData: localFlowData,
      editorBaseUpdatedAt: localEditorBaseUpdatedAt,
      hydratedFromRefetch: false,
      reason: 'refetch_parse_failed',
    }
  }
}
